import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { authAxios, API, useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Sparkles, Calendar, KeyRound, Sun, Moon, Loader2, Check, AlertTriangle, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  } catch { return iso; }
}

function planBadge(daysLeft) {
  if (daysLeft == null) return { label: "Sin plan", color: "bg-slate-200 text-slate-600" };
  if (daysLeft < 0) return { label: "Vencido", color: "bg-red-100 text-red-700" };
  if (daysLeft <= 3) return { label: `Vence en ${daysLeft} día${daysLeft === 1 ? "" : "s"}`, color: "bg-amber-100 text-amber-700" };
  return { label: "Activo", color: "bg-emerald-100 text-emerald-700" };
}

export default function UserSettingsDialog({ open, onOpenChange }) {
  const { user, refreshMe } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const [me, setMe] = useState(user || {});
  const [pwdCurrent, setPwdCurrent] = useState("");
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdMsg, setPwdMsg] = useState("");
  const [pwdErr, setPwdErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setPwdCurrent(""); setPwdNew(""); setPwdConfirm(""); setPwdMsg(""); setPwdErr("");
    authAxios.get(`${API}/auth/me`).then(({ data }) => setMe(data)).catch(() => {});
  }, [open]);

  const expiresOn = me.plan_expires_on;
  const daysLeft = expiresOn ? Math.ceil((new Date(expiresOn).getTime() - Date.now()) / 86400000) : null;
  const badge = planBadge(daysLeft);

  const submitPassword = async (e) => {
    e.preventDefault();
    setPwdMsg(""); setPwdErr("");
    if (pwdNew.length < 6) { setPwdErr("La nueva contraseña debe tener al menos 6 caracteres"); return; }
    if (pwdNew !== pwdConfirm) { setPwdErr("Las contraseñas no coinciden"); return; }
    setPwdSaving(true);
    try {
      await authAxios.post(`${API}/auth/change-password`, { current_password: pwdCurrent, new_password: pwdNew });
      setPwdMsg("Contraseña actualizada correctamente");
      setPwdCurrent(""); setPwdNew(""); setPwdConfirm("");
    } catch (err) {
      setPwdErr(err.response?.data?.detail || err.message);
    } finally { setPwdSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="user-settings-dialog">
        <DialogHeader>
          <DialogTitle>Mi cuenta</DialogTitle>
          <DialogDescription>{me.email}</DialogDescription>
        </DialogHeader>

        {/* Plan section */}
        <div className="border rounded-xl p-4 bg-slate-50 dark:bg-slate-800/40">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="text-xs uppercase font-semibold tracking-wider text-slate-500 dark:text-slate-400">Mi plan</span>
              </div>
              <p className="text-lg font-semibold mt-1" data-testid="settings-plan-name">{me.active_plan_name || "Sin plan activo"}</p>
              {me.active_plan_slug && (
                <p className="text-xs text-slate-500">Slug: <code>{me.active_plan_slug}</code></p>
              )}
            </div>
            <span className={`text-xs px-2 py-1 rounded-full font-medium ${badge.color}`} data-testid="settings-plan-badge">{badge.label}</span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
            <div>
              <p className="text-xs text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Vigencia hasta</p>
              <p className="font-medium" data-testid="settings-plan-expires">{fmtDate(expiresOn)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Estado de pago</p>
              <p className="font-medium">{daysLeft == null ? "—" : daysLeft < 0 ? "Vencido" : "Pagado"}</p>
            </div>
          </div>
          <Button
            onClick={() => { onOpenChange(false); navigate("/dashboard/billing"); }}
            variant="outline"
            size="sm"
            className="mt-3 w-full"
            data-testid="settings-go-billing"
          >
            <ExternalLink className="w-3 h-3 mr-1" /> {daysLeft != null && daysLeft < 7 ? "Renovar plan" : "Cambiar / mejorar plan"}
          </Button>
        </div>

        {/* Theme toggle */}
        <div className="border rounded-xl p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              {theme === "dark" ? <Moon className="w-5 h-5 text-indigo-500" /> : <Sun className="w-5 h-5 text-amber-500" />}
              <div>
                <p className="font-medium">Modo {theme === "dark" ? "oscuro" : "claro"}</p>
                <p className="text-xs text-slate-500">Cambia el aspecto del panel.</p>
              </div>
            </div>
            <Switch
              checked={theme === "dark"}
              onCheckedChange={(v) => setTheme(v ? "dark" : "light")}
              data-testid="settings-theme-toggle"
            />
          </div>
        </div>

        {/* Change password */}
        <form onSubmit={submitPassword} className="border rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-slate-500" />
            <p className="font-medium">Cambiar contraseña</p>
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Contraseña actual</Label>
            <Input type="password" value={pwdCurrent} onChange={(e) => setPwdCurrent(e.target.value)} required data-testid="settings-pwd-current" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Nueva contraseña</Label>
            <Input type="password" value={pwdNew} onChange={(e) => setPwdNew(e.target.value)} required minLength={6} data-testid="settings-pwd-new" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">Repetir nueva contraseña</Label>
            <Input type="password" value={pwdConfirm} onChange={(e) => setPwdConfirm(e.target.value)} required minLength={6} data-testid="settings-pwd-confirm" />
          </div>
          {pwdErr && <p className="text-xs text-red-500 flex items-center gap-1" data-testid="settings-pwd-error"><AlertTriangle className="w-3 h-3" /> {pwdErr}</p>}
          {pwdMsg && <p className="text-xs text-emerald-600 flex items-center gap-1" data-testid="settings-pwd-success"><Check className="w-3 h-3" /> {pwdMsg}</p>}
          <Button type="submit" disabled={pwdSaving} className="w-full" data-testid="settings-pwd-submit">
            {pwdSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Actualizar contraseña
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
